export interface Permissions {
  /**
   * Whether the user can access the admin HQ.
   */
  adminAccess: boolean

  /**
   * Whether the user can assign teams to jobs as an admin.
   */
  adminAssignTeam: boolean

  /**
   * Whether the user can manage billing as an admin.
   */
  adminBillingManage: boolean

  /**
   * Whether the user can impersonate clients.
   */
  adminClientImpersonate: boolean

  /**
   * Whether the user can list client users as an admin.
   */
  adminClientUserList: boolean

  /**
   * Whether the user can manage job conversations as an admin.
   */
  adminConversationManage: boolean

  /**
   * Whether the user can manage documents as an admin.
   */
  adminDocumentManage: boolean

  /**
   * Whether the user can manage account merge requests.
   */
  adminGlobalManageAccountMergeRequests: boolean

  /**
   * Whether the user can list users across all workspaces.
   */
  adminGlobalUserList: boolean

  /**
   * Whether the user can amend jobs as an admin.
   */
  adminJobAmend: boolean

  /**
   * Whether the user can manage the champion pro of jobs as an admin.
   */
  adminJobChampionProviderManage: boolean

  /**
   * Whether the user can manage the clients of jobs as an admin.
   */
  adminJobClientsManage: boolean

  /**
   * Whether the user can close jobs as an admin.
   */
  adminJobClose: boolean

  /**
   * Whether the user can create jobs as an admin.
   */
  adminJobCreate: boolean

  /**
   * Whether the user can delete jobs as an admin.
   */
  adminJobDelete: boolean

  /**
   * Whether the user can list all jobs as an admin.
   */
  adminJobList: boolean

  /**
   * Whether the user can put jobs on hold as an admin.
   */
  adminJobOnHold: boolean

  /**
   * Whether the user can approve drafts in the job progression as an admin.
   */
  adminJobProgressionApproveDraft: boolean

  /**
   * Whether the user can reopen closed jobs as an admin.
   */
  adminJobReopen: boolean

  /**
   * Whether the user can manage job scope change requests as an admin.
   */
  adminJobScopeChangeManage: boolean

  /**
   * Whether the user can resolve job scope change requests as an admin.
   */
  adminJobScopeChangeResolve: boolean

  /**
   * Whether the user can manage the supporting pros of jobs as an admin.
   */
  adminJobSupportingProviderManage: boolean

  /**
   * Whether the user can transfer jobs as an admin.
   */
  adminJobTransfer: boolean

  /**
   * Whether the user can update jobs as an admin.
   */
  adminJobUpdate: boolean

  /**
   * Whether the user can view any job as an admin.
   */
  adminJobView: boolean

  /**
   * Whether the user can edit and publish legends as an admin.
   */
  adminLegendModify: boolean

  /**
   * Whether the user can view legends as an admin.
   */
  adminLegendView: boolean

  /**
   * Whether the user can manage the global inventory.
   */
  adminManageGlobalInventoryItems: boolean

  /**
   * Whether the user can manage OCR scoping jobs.
   */
  adminManageOcrJobs: boolean

  /**
   * Whether the user can manage team members as an admin.
   */
  adminManageTeamMember: boolean

  /**
   * Whether the user can view the pro cut as an admin.
   */
  adminProCutView: boolean

  /**
   * Whether the user can view pros' personal information as an admin.
   */
  adminProPiView: boolean

  /**
   * Whether the user can impersonate pros.
   */
  adminProviderImpersonate: boolean

  /**
   * Whether the user can list roles as an admin.
   */
  adminRoleList: boolean

  /**
   * Whether the user can view scoping work as an admin.
   */
  adminScopeWorkView: boolean

  /**
   * Whether the user can manage skills as an admin.
   */
  adminSkillsManage: boolean

  /**
   * Whether the user can split job payouts.
   */
  adminSplitPayouts: boolean

  /**
   * Whether the user can manage staffing.
   */
  adminStaffingManage: boolean

  /**
   * Whether the user can view all teams as an admin.
   */
  adminTeamsView: boolean

  /**
   * Whether the user can create users as an admin.
   */
  adminUserCreate: boolean

  /**
   * Whether the user can list users as an admin.
   */
  adminUserList: boolean

  /**
   * Whether the user can modify users as an admin.
   */
  adminUserModify: boolean

  /**
   * Whether the user can read users as an admin.
   */
  adminUserRead: boolean

  /**
   * Whether the user can modify user roles as an admin.
   */
  adminUserRolesModify: boolean

  /**
   * Whether the user can view pricing as an admin.
   */
  adminViewPricing: boolean

  /**
   * Whether the user can view the TXF Worklayer job as an admin.
   */
  adminViewTxfWorklayerJob: boolean

  /**
   * Whether the user can send messages.
   */
  chatAdd: boolean

  /**
   * Whether the user can view conversations.
   */
  chatView: boolean

  /**
   * Whether the user can manage client assignments.
   */
  clientAssignmentManage: boolean

  /**
   * Whether the user can create teams.
   */
  createTeam: boolean

  /**
   * Whether the user can create jobs for a team.
   */
  createTeamJobs: boolean

  /**
   * Whether the user can create customers.
   */
  customerCreate: boolean

  /**
   * Whether the user can remove documents uploaded by clients.
   */
  docsClientRemove: boolean

  /**
   * Whether the user can remove documents uploaded by pros.
   */
  docsProviderRemove: boolean

  /**
   * Whether the user can remove documents the user uploaded.
   */
  docsSelfRemove: boolean

  /**
   * Whether the user can upload documents.
   */
  docsUpload: boolean

  /**
   * Whether the user can view documents.
   */
  docsView: boolean

  /**
   * Whether the user can view job insights.
   */
  insightsView: boolean

  /**
   * Whether the user can close jobs.
   */
  jobClose: boolean

  /**
   * Whether the user can forfeit jobs.
   */
  jobForfeit: boolean

  /**
   * Whether the user can put jobs on hold.
   */
  jobOnHold: boolean

  /**
   * Whether the user can pick up jobs.
   */
  jobPickup: boolean

  /**
   * Whether the user can rename jobs as a pro.
   */
  jobProviderUpdateName: boolean

  /**
   * Whether the user can request job scope changes.
   */
  jobScopeChangeCreate: boolean

  /**
   * Whether the user can update jobs.
   */
  jobUpdate: boolean

  /**
   * Whether the user can manage bookkeeping.
   */
  manageBookkeeping: boolean

  /**
   * Whether the user can manage a team's billing.
   */
  manageTeamBilling: boolean

  /**
   * Whether the user can manage a team's members.
   */
  manageTeamMember: boolean

  /**
   * Whether the user can manage the workspace inventory.
   */
  manageWorkspaceInventoryItems: boolean

  /**
   * Whether the user can scope work.
   */
  scopeWork: boolean

  /**
   * Whether the user can start a new job.
   */
  startNewJob: boolean

  /**
   * Whether the user can start jobs for a team.
   */
  startTeamJobs: boolean

  /**
   * Whether the user can approve drafts in the job progression as a supporting client.
   */
  supportingClientJobProgressionApproveDraft: boolean

  /**
   * Whether the user can update their own skills.
   */
  updateOwnSkills: boolean

  /**
   * Whether the user can update a team's jobs.
   */
  updateTeamJobs: boolean

  /**
   * Whether the user can view client details.
   */
  viewClientDetails: boolean

  /**
   * Whether the user can view contract amounts.
   */
  viewContractAmount: boolean

  /**
   * Whether the user can view the firm dashboard.
   */
  viewFirmDashboard: boolean

  /**
   * Whether the user can view job pricing.
   */
  viewJobPricing: boolean

  /**
   * Whether the user can view opportunities.
   */
  viewOpportunities: boolean

  /**
   * Whether the user can view a team's jobs.
   */
  viewTeamJobs: boolean

  /**
   * Whether the user can manage client assignments across the workspace.
   */
  workspaceClientAssignmentManage: boolean

  /**
   * Whether the user can update the information gathered for jobs.
   */
  workspaceJobInfoGatheringUpdate: boolean

  /**
   * Whether the user can manage job notes.
   */
  workspaceJobNoteManage: boolean
}

export const Permissions = {
  /**
   * Returns an empty permission set.
   */
  empty: (): Permissions => ({
    adminAccess: false,
    adminAssignTeam: false,
    adminBillingManage: false,
    adminClientImpersonate: false,
    adminClientUserList: false,
    adminConversationManage: false,
    adminDocumentManage: false,
    adminGlobalManageAccountMergeRequests: false,
    adminGlobalUserList: false,
    adminJobAmend: false,
    adminJobChampionProviderManage: false,
    adminJobClientsManage: false,
    adminJobClose: false,
    adminJobCreate: false,
    adminJobDelete: false,
    adminJobList: false,
    adminJobOnHold: false,
    adminJobProgressionApproveDraft: false,
    adminJobReopen: false,
    adminJobScopeChangeManage: false,
    adminJobScopeChangeResolve: false,
    adminJobSupportingProviderManage: false,
    adminJobTransfer: false,
    adminJobUpdate: false,
    adminJobView: false,
    adminLegendModify: false,
    adminLegendView: false,
    adminManageGlobalInventoryItems: false,
    adminManageOcrJobs: false,
    adminManageTeamMember: false,
    adminProCutView: false,
    adminProPiView: false,
    adminProviderImpersonate: false,
    adminRoleList: false,
    adminScopeWorkView: false,
    adminSkillsManage: false,
    adminSplitPayouts: false,
    adminStaffingManage: false,
    adminTeamsView: false,
    adminUserCreate: false,
    adminUserList: false,
    adminUserModify: false,
    adminUserRead: false,
    adminUserRolesModify: false,
    adminViewPricing: false,
    adminViewTxfWorklayerJob: false,
    chatAdd: false,
    chatView: false,
    clientAssignmentManage: false,
    createTeam: false,
    createTeamJobs: false,
    customerCreate: false,
    docsClientRemove: false,
    docsProviderRemove: false,
    docsSelfRemove: false,
    docsUpload: false,
    docsView: false,
    insightsView: false,
    jobClose: false,
    jobForfeit: false,
    jobOnHold: false,
    jobPickup: false,
    jobProviderUpdateName: false,
    jobScopeChangeCreate: false,
    jobUpdate: false,
    manageBookkeeping: false,
    manageTeamBilling: false,
    manageTeamMember: false,
    manageWorkspaceInventoryItems: false,
    scopeWork: false,
    startNewJob: false,
    startTeamJobs: false,
    supportingClientJobProgressionApproveDraft: false,
    updateOwnSkills: false,
    updateTeamJobs: false,
    viewClientDetails: false,
    viewContractAmount: false,
    viewFirmDashboard: false,
    viewJobPricing: false,
    viewOpportunities: false,
    viewTeamJobs: false,
    workspaceClientAssignmentManage: false,
    workspaceJobInfoGatheringUpdate: false,
    workspaceJobNoteManage: false,
  }),
}
